import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Mail, MailOpen, Trash2, RefreshCw, Inbox as InboxIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
}

export default function AdminInbox() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("all"); // all | unread

    const refresh = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/admin/inbox${filter === "unread" ? "?unread_only=true" : ""}`);
            setItems(data.items);
            setUnread(data.unread);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load inbox");
        } finally {
            setLoading(false);
        }
    }, [filter]);
    useEffect(() => { refresh(); }, [refresh]);

    const open = async (id) => {
        try {
            const { data } = await api.get(`/admin/inbox/${id}`);
            setSelected(data);
            if (!data.read) {
                await api.post(`/admin/inbox/${id}/read`);
                refresh();
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this message?")) return;
        try {
            await api.delete(`/admin/inbox/${id}`);
            if (selected?.email_id === id) setSelected(null);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <>
            <Helmet><title>Inbox — OpenCSV Admin</title></Helmet>
            <section className="mx-auto max-w-7xl px-4 md:px-8 py-10">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ admin / inbox</div>
                        <h1 className="font-display font-black tracking-tighter text-4xl flex items-center gap-3">
                            <InboxIcon size={28} /> Inbox
                            {unread > 0 && (
                                <span className="ui-heading text-xs uppercase tracking-widest bg-cobalt text-white px-2 py-1" data-testid="inbox-unread-badge">{unread} unread</span>
                            )}
                        </h1>
                        <p className="mt-2 text-sm text-ink-soft">Replies and emails sent to <span className="font-mono">hello@opencsv.io</span> arrive here.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="border border-ink/15 inline-flex" role="tablist">
                            <button onClick={() => setFilter("all")} className={`px-3 py-2 ui-heading text-xs uppercase tracking-widest ${filter === "all" ? "bg-ink text-white" : "bg-white"}`} data-testid="filter-all">All</button>
                            <button onClick={() => setFilter("unread")} className={`px-3 py-2 ui-heading text-xs uppercase tracking-widest ${filter === "unread" ? "bg-ink text-white" : "bg-white"}`} data-testid="filter-unread">Unread</button>
                        </div>
                        <button onClick={refresh} className="btn-brutal border border-ink/20 px-3 py-2 ui-heading text-xs inline-flex items-center gap-2" data-testid="inbox-refresh">
                            <RefreshCw size={14} /> Refresh
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-ink/10 border border-ink/10 min-h-[60vh]">
                    <div className="md:col-span-5 bg-white" data-testid="inbox-list">
                        {loading ? (
                            <div className="p-6 text-center text-ink-muted text-sm">Loading…</div>
                        ) : items.length === 0 ? (
                            <div className="p-10 text-center">
                                <Mail size={28} className="mx-auto text-ink-muted mb-3" />
                                <div className="ui-heading text-sm text-ink-soft">No messages yet.</div>
                                <p className="mt-1 text-xs text-ink-muted">Once your MX records point at the inbound provider, anything sent to your domain shows up here.</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-black/10">
                                {items.map((m) => (
                                    <li key={m.email_id}>
                                        <button onClick={() => open(m.email_id)} className={`w-full text-left px-4 py-3 hover:bg-paper transition-colors duration-100 ${selected?.email_id === m.email_id ? "bg-paper" : ""}`} data-testid={`inbox-item-${m.email_id}`}>
                                            <div className="flex items-center gap-2">
                                                {m.read ? <MailOpen size={14} className="text-ink-muted" /> : <Mail size={14} className="text-cobalt" />}
                                                <span className={`text-sm flex-1 truncate ${m.read ? "text-ink-soft" : "text-ink font-semibold"}`}>{m.from || "(unknown)"}</span>
                                                <span className="text-[11px] text-ink-muted">{timeAgo(m.received_at)}</span>
                                            </div>
                                            <div className={`text-sm mt-1 truncate ${m.read ? "text-ink-soft" : "text-ink"}`}>{m.subject}</div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="md:col-span-7 bg-white p-6" data-testid="inbox-detail">
                        {!selected ? (
                            <div className="text-center text-ink-muted text-sm py-16">Select a message to read it.</div>
                        ) : (
                            <article>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <h2 className="font-display font-black tracking-tighter text-2xl break-words" data-testid="inbox-subject">{selected.subject}</h2>
                                        <div className="mt-2 text-sm text-ink-soft"><span className="ui-heading text-xs uppercase tracking-widest text-ink-muted mr-2">from</span><span className="font-mono">{selected.from}</span></div>
                                        {selected.to && <div className="text-sm text-ink-soft"><span className="ui-heading text-xs uppercase tracking-widest text-ink-muted mr-2">to</span><span className="font-mono">{selected.to}</span></div>}
                                        <div className="text-xs text-ink-muted mt-1">{timeAgo(selected.received_at)}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {selected.from && (
                                            <a href={`mailto:${selected.from.match(/<([^>]+)>/)?.[1] || selected.from}?subject=Re: ${encodeURIComponent(selected.subject || "")}`} className="btn-brutal bg-ink text-white px-3 py-2 ui-heading text-xs uppercase tracking-widest" data-testid="inbox-reply">Reply</a>
                                        )}
                                        <button onClick={() => remove(selected.email_id)} className="btn-brutal border border-signal-red/40 text-signal-red px-3 py-2" data-testid="inbox-delete" aria-label="Delete">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <hr className="my-4 border-black/10" />
                                {selected.html ? (
                                    <iframe title="email-body" sandbox="" className="w-full min-h-[60vh] border border-black/10" srcDoc={selected.html} data-testid="inbox-html" />
                                ) : (
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-ink leading-relaxed" data-testid="inbox-text">{selected.text || "(empty body)"}</pre>
                                )}
                            </article>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
